package er.prateek.crossly.ui.screens.crossword

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.coach_play_cell_body
import crossly.shared.generated.resources.coach_play_cell_title
import crossly.shared.generated.resources.coach_play_hint_body
import crossly.shared.generated.resources.coach_play_hint_title
import crossly.shared.generated.resources.coach_play_letters_body
import crossly.shared.generated.resources.coach_play_letters_title
import er.prateek.crossly.ui.coach.CoachOverlay
import er.prateek.crossly.ui.coach.toOverlayLocal
import er.prateek.crossly.ui.components.IconHand
import er.prateek.crossly.ui.theme.game
import org.jetbrains.compose.resources.stringResource
import kotlin.math.PI
import kotlin.math.roundToInt
import kotlin.math.sin

/**
 * First-run play tutorial: a spotlight on the live UI plus a sticker card that
 * speaks in the same voice as the rest of the game.
 */
@Composable
fun PlayCoachOverlay(
    step: PlayCoachStep,
    anchors: CoachMarkAnchors,
    selectedCell: Rect,
    onNext: () -> Unit,
    onSkip: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val copy = copyFor(step)
    CoachOverlay(
        stepIndex = step.index,
        stepCount = PlayCoachStep.count,
        title = copy.title,
        body = copy.body,
        hole = anchors.holeFor(step, selectedCell),
        onNext = onNext,
        onSkip = onSkip,
        modifier = modifier,
        holeCornerRadius = when (step) {
            PlayCoachStep.HINT -> 18.dp
            PlayCoachStep.CELL -> 10.dp
            PlayCoachStep.LETTERS -> 14.dp
        },
        holePad = when (step) {
            PlayCoachStep.HINT -> 6.dp
            PlayCoachStep.CELL -> 8.dp
            PlayCoachStep.LETTERS -> 8.dp
        },
    ) { overlayOrigin ->
        if (step == PlayCoachStep.LETTERS && !anchors.letters.isEmpty && !anchors.board.isEmpty) {
            CoachDragDemo(
                from = anchors.letters.toOverlayLocal(overlayOrigin).center,
                to = run {
                    val cell = selectedCell.toOverlayLocal(overlayOrigin)
                    if (cell.isEmpty) anchors.board.toOverlayLocal(overlayOrigin).center else cell.center
                },
            )
        }
    }
}

private data class CoachCopy(val title: String, val body: String)

@Composable
private fun copyFor(step: PlayCoachStep): CoachCopy = when (step) {
    PlayCoachStep.HINT -> CoachCopy(
        title = stringResource(Res.string.coach_play_hint_title),
        body = stringResource(Res.string.coach_play_hint_body),
    )
    PlayCoachStep.CELL -> CoachCopy(
        title = stringResource(Res.string.coach_play_cell_title),
        body = stringResource(Res.string.coach_play_cell_body),
    )
    PlayCoachStep.LETTERS -> CoachCopy(
        title = stringResource(Res.string.coach_play_letters_title),
        body = stringResource(Res.string.coach_play_letters_body),
    )
}

/**
 * A looping letter toss from the rack toward a square — the motion the player
 * is being asked to copy, without requiring them to try it during the tour.
 */
@Composable
private fun CoachDragDemo(from: Offset, to: Offset) {
    val colors = MaterialTheme.game
    val density = LocalDensity.current
    val tile = 40.dp
    val half = with(density) { tile.toPx() / 2f }
    val lift = with(density) { 36.dp.toPx() }

    val travel = rememberInfiniteTransition(label = "coachDrag")
    val t by travel.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(1600, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Restart,
        ),
        label = "coachDragT",
    )

    val p = t
    val center = Offset(
        x = from.x + (to.x - from.x) * p,
        y = from.y + (to.y - from.y) * p - sin(p * PI).toFloat() * lift,
    )
    val fade = when {
        p < 0.12f -> p / 0.12f
        p > 0.82f -> ((1f - p) / 0.18f).coerceAtLeast(0f)
        else -> 1f
    }
    val shape = RoundedCornerShape(10.dp)

    Box(
        modifier = Modifier
            .offset { IntOffset((center.x - half).roundToInt(), (center.y - half).roundToInt()) }
            .width(tile)
            .height(tile)
            .clip(shape)
            .background(colors.cellFocus.copy(alpha = 0.55f + 0.4f * fade))
            .border(2.dp, colors.accent.copy(alpha = fade), shape),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = "A",
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
            color = colors.cellText.copy(alpha = fade),
            textAlign = TextAlign.Center,
        )
        Box(
            modifier = Modifier
                .align(Alignment.BottomEnd)
                .offset(x = 10.dp, y = 12.dp),
        ) {
            IconHand(size = 22.dp, tint = colors.gold.copy(alpha = fade))
        }
    }
}
